/**
 * 1.7.5 — the one chart of the app (D-1.7.5-1). Four shapes — bar, horizontal bar, line, donut — drawn as inline
 * SVG written here: no chart library, nothing fetched. Flat drawings only; there is no other kind and no option.
 *
 * - `data-testid` on the figure is `testId` (default `chart`), with `data-kind` = bar | hbar | line | donut.
 * - The numbers are always in the DOM as ONE table (`chart-table`, built by `chartTable`), read by a screen reader
 *   and hidden from sight while the drawing shows; the drawing itself is hidden from assistive technology.
 *   "Show as a table" (`chart-toggle`, aria-pressed) shows the table in place of the drawing; on paper both show,
 *   the table under the drawing (src/styles/print175.css).
 * - Colour is never the only cue: every bar, point and slice has its value written beside it; with more than one
 *   series each has a pattern of its own (`patternOf`), lines differ by dash and point shape, and a legend in
 *   words (`chart-legend`) carries the same swatch. One series is drawn solid and reads by its labels alone.
 * - Drawn at real pixels from the figure's measured width, with text sized in rem by the stylesheet
 *   (src/styles/chart175.css) — so the app's text-size setting changes the words and nothing is scaled.
 */
import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { chartIsEmpty, chartMax, chartNumber, chartScale, chartTable, donutSlices, patternOf, ringPath, type ChartPattern, type ChartSpec } from '@shared/charts';

export interface ChartProps {
  spec: ChartSpec;
  /** The figure's test id (default `chart`). */
  testId?: string;
  /** The drawing's height in px at standard text size (default 240; a horizontal bar chart grows with its rows). */
  height?: number;
  /** The heading level of the title (default 3). */
  level?: 2 | 3 | 4;
}

/* ------------------------------------------------------------------------------------------------
 * Measuring: the width the drawing has, and what 1rem is today (the text-size setting moves it)
 * ---------------------------------------------------------------------------------------------- */

interface Box {
  width: number;
  rem: number;
}
/** Where nothing can be measured (a test, a first paint): a plain desktop card. */
const FALLBACK: Box = { width: 640, rem: 16 };
/** The root's font size at the app's standard text size (src/styles/tokens.css). */
const STANDARD_REM = 17;

function useBox(ref: RefObject<HTMLElement | null>): Box {
  const [box, setBox] = useState<Box>(FALLBACK);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => {
      const w = el.clientWidth;
      const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || FALLBACK.rem;
      // hidden (the table is showing) measures 0: keep the width it had
      setBox((prev) => {
        const width = w > 0 ? w : prev.width;
        return prev.width === width && prev.rem === rem ? prev : { width, rem };
      });
    };
    read();
    if (typeof ResizeObserver === 'undefined') return;
    const sizes = new ResizeObserver(read);
    sizes.observe(el);
    const setting = typeof MutationObserver === 'undefined' ? null : new MutationObserver(read);
    setting?.observe(document.documentElement, { attributes: true, attributeFilter: ['data-size', 'style', 'class'] });
    return () => {
      sizes.disconnect();
      setting?.disconnect();
    };
  }, [ref]);
  return box;
}

/* ------------------------------------------------------------------------------------------------
 * Words inside the drawing: an estimate of their width, and wrapping
 * ---------------------------------------------------------------------------------------------- */

/** The stylesheet's sizes, in rem — the layout leaves room by the same numbers. */
const TEXT = { axis: 0.75, value: 0.8, name: 0.82, total: 1.7 } as const;
/** About how wide a letter is, as a share of its font size (Manrope, with room to spare). */
const LETTER = 0.6;

function wrap(text: string, maxChars: number, maxLines: number): string[] {
  const room = Math.max(3, Math.floor(maxChars));
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(/\s+/).filter(Boolean)) {
    const next = line ? `${line} ${word}` : word;
    if (next.length <= room || !line) line = next;
    else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  const cut = (s: string) => (s.length > room ? `${s.slice(0, room - 1)}…` : s);
  if (lines.length <= maxLines) return lines.map(cut);
  const kept = lines.slice(0, maxLines);
  const last = kept[maxLines - 1]!;
  kept[maxLines - 1] = last.length + 1 > room ? `${last.slice(0, room - 1)}…` : `${last}…`;
  return kept.map(cut);
}

/* ------------------------------------------------------------------------------------------------
 * A series' look: its colour, its pattern, its dash and its point
 * ---------------------------------------------------------------------------------------------- */

const COLOURS = 6;
const DASHES: (string | undefined)[] = [undefined, '7 4', '2 4', '10 4 2 4', '1 5', '14 5'];

interface Look {
  index: number;
  colour: number;
  pattern: ChartPattern;
}
/** One series (one slice, alone) is solid; several each take the next colour and the next pattern. */
const lookOf = (index: number, several: boolean): Look => (several ? { index, colour: index % COLOURS, pattern: patternOf(index) } : { index, colour: 0, pattern: 'solid' });

/** The pattern's tile: the series colour underneath, the page's surface drawn over it. */
function PatternDef({ id, look }: { id: string; look: Look }) {
  if (look.pattern === 'solid') return null;
  const over: Record<Exclude<ChartPattern, 'solid'>, ReactNode> = {
    stripes: <path className="chart-over-line" d="M-2 2 L2 -2 M0 8 L8 0 M6 10 L10 6" strokeWidth={2.2} />,
    dots: (
      <>
        <circle className="chart-over-fill" cx={2} cy={2} r={1.5} />
        <circle className="chart-over-fill" cx={6} cy={6} r={1.5} />
      </>
    ),
    cross: <path className="chart-over-line" d="M0 0 L8 8 M8 0 L0 8" strokeWidth={1.3} />,
    rings: <circle className="chart-over-line" cx={4} cy={4} r={2.4} strokeWidth={1.3} />,
    grid: <path className="chart-over-line" d="M4 0 V8 M0 4 H8" strokeWidth={1.3} />,
  };
  return (
    <pattern id={id} patternUnits="userSpaceOnUse" width={8} height={8}>
      <rect className={`chart-f${look.colour}`} width={8} height={8} />
      {over[look.pattern]}
    </pattern>
  );
}

/** What a filled mark carries: its series, its pattern, its colour — and the outline that keeps its edge clear. */
function fillProps(look: Look, uid: string, more = ''): { className: string; fill?: string } {
  const solid = look.pattern === 'solid';
  return {
    className: `chart-mark chart-s${look.colour}${solid ? ` chart-f${look.colour}` : ''}${more ? ` ${more}` : ''}`,
    ...(solid ? {} : { fill: `url(#${uid}-p${look.index})` }),
  };
}
/** What every mark says of itself: which series it is, and its pattern. */
const markProps = (look: Look) => ({ 'data-mark': '', 'data-series': look.index, 'data-pattern': look.pattern });

/** A line's point: a shape of its own for each series. */
function Point({ look, x, y, r, mark = true }: { look: Look; x: number; y: number; r: number; mark?: boolean }) {
  const common = mark ? markProps(look) : {};
  const filled = `chart-point chart-f${look.colour} chart-s${look.colour}`;
  switch (look.pattern) {
    case 'solid':
      return <circle {...common} className={filled} cx={x} cy={y} r={r} />;
    case 'stripes':
      return <rect {...common} className={filled} x={x - r} y={y - r} width={r * 2} height={r * 2} />;
    case 'dots':
      return <path {...common} className={filled} d={`M ${x} ${y - r * 1.25} L ${x + r * 1.2} ${y + r} L ${x - r * 1.2} ${y + r} Z`} />;
    case 'cross':
      return <path {...common} className={`chart-point chart-point-open chart-s${look.colour}`} d={`M ${x - r} ${y - r} L ${x + r} ${y + r} M ${x + r} ${y - r} L ${x - r} ${y + r}`} />;
    case 'rings':
      return <circle {...common} className={`chart-point chart-point-open chart-s${look.colour}`} cx={x} cy={y} r={r} />;
    default:
      return <path {...common} className={filled} d={`M ${x} ${y - r * 1.3} L ${x + r * 1.3} ${y} L ${x} ${y + r * 1.3} L ${x - r * 1.3} ${y} Z`} />;
  }
}

/** The legend's swatch: the same colour and pattern (for a line: the same dash and point). */
function Swatch({ look, uid, line }: { look: Look; uid: string; line: boolean }) {
  if (line) {
    return (
      <svg className="chart-swatch" width={36} height={16} viewBox="0 0 36 16" aria-hidden="true" focusable="false">
        <path className={`chart-line chart-s${look.colour}`} d="M1 8 H35" strokeDasharray={DASHES[look.index % DASHES.length]} />
        <Point look={look} x={18} y={8} r={4} mark={false} />
      </svg>
    );
  }
  return (
    <svg className="chart-swatch" width={18} height={18} viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <rect {...fillProps(look, uid)} x={1} y={1} width={16} height={16} rx={4} />
    </svg>
  );
}

/* ------------------------------------------------------------------------------------------------
 * The four drawings
 * ---------------------------------------------------------------------------------------------- */

interface DrawProps {
  spec: ChartSpec;
  box: Box;
  height: number;
  uid: string;
}
interface Drawing {
  width: number;
  height: number;
  body: ReactNode;
}

const value = (spec: ChartSpec, s: number, i: number): number => Math.max(0, spec.series[s]?.values[i] || 0);
const round = (n: number) => Math.round(n * 100) / 100;

/** A bar standing on the baseline, its top corners rounded. */
function barUp(x: number, base: number, w: number, h: number): string {
  const r = Math.min(6, w / 2, h);
  const top = base - h;
  return `M ${round(x)} ${round(base)} V ${round(top + r)} Q ${round(x)} ${round(top)} ${round(x + r)} ${round(top)} H ${round(x + w - r)} Q ${round(x + w)} ${round(top)} ${round(x + w)} ${round(top + r)} V ${round(base)} Z`;
}
/** A bar lying from the left edge, its far end rounded. */
function barAcross(x: number, y: number, w: number, h: number): string {
  const r = Math.min(6, h / 2, w);
  return `M ${round(x)} ${round(y)} H ${round(x + w - r)} Q ${round(x + w)} ${round(y)} ${round(x + w)} ${round(y + r)} V ${round(y + h - r)} Q ${round(x + w)} ${round(y + h)} ${round(x + w - r)} ${round(y + h)} H ${round(x)} Z`;
}

/** The value axis of a bar or a line chart: the gridlines and their numbers. */
function axis(spec: ChartSpec, box: Box, height: number) {
  const { rem, width } = box;
  const scale = chartScale(chartMax(spec));
  const axisText = TEXT.axis * rem;
  const left = Math.ceil(chartNumber(scale.max).length * axisText * LETTER + 0.7 * rem);
  const right = Math.ceil(0.6 * rem);
  const top = Math.ceil(TEXT.value * rem * 1.9);
  const nameLine = TEXT.name * rem * 1.25;
  const bottom = Math.ceil(nameLine * 2 + 0.6 * rem);
  const plotW = Math.max(40, width - left - right);
  const plotH = Math.max(60, height - top - bottom);
  const y = (v: number) => top + plotH - (Math.max(0, v) / scale.max) * plotH;
  const grid = scale.ticks.map((tick) => (
    <g key={tick}>
      <line className={tick === 0 ? 'chart-base' : 'chart-grid'} x1={left} x2={left + plotW} y1={round(y(tick))} y2={round(y(tick))} />
      <text className="chart-t" x={left - 0.4 * rem} y={round(y(tick))} dy="0.32em" textAnchor="end">
        {chartNumber(tick)}
      </text>
    </g>
  ));
  return { left, top, plotW, plotH, y, grid, nameLine, base: top + plotH, total: top + plotH + bottom };
}

/** The names under a bar or a line chart: every one when they fit, every few when they do not. */
function names(spec: ChartSpec, rem: number, x: (i: number) => number, step: number, base: number, nameLine: number): ReactNode {
  const letter = TEXT.name * rem * LETTER;
  const fit = Math.max(1, Math.ceil((4 * letter + 6) / Math.max(1, step)));
  return spec.categories.map((c, i) => {
    if (i % fit !== 0) return null;
    const lines = wrap(c, (step * fit - 6) / letter, 2);
    return (
      <text key={i} className="chart-n" data-x={i} x={round(x(i))} y={round(base + 0.45 * rem)} textAnchor="middle">
        {lines.map((line, n) => (
          <tspan key={n} x={round(x(i))} dy={n === 0 ? nameLine * 0.8 : nameLine}>
            {line}
          </tspan>
        ))}
      </text>
    );
  });
}

function drawBars({ spec, box, height, uid }: DrawProps): Drawing {
  const a = axis(spec, box, height);
  const count = spec.categories.length;
  const several = spec.series.length > 1;
  const group = a.plotW / count;
  const bar = Math.max(2, Math.min((group * 0.74) / spec.series.length, 3.4 * box.rem));
  const gap = several ? Math.min(3, bar * 0.12) : 0;
  const used = bar * spec.series.length;
  const centre = (i: number) => a.left + group * i + group / 2;
  const body = (
    <>
      {a.grid}
      {spec.categories.map((_, i) =>
        spec.series.map((_s, s) => {
          const v = value(spec, s, i);
          const x = centre(i) - used / 2 + bar * s + gap / 2;
          // nothing is still a mark: a sliver on the baseline, with its 0 over it
          const h = Math.max(1.5, a.base - a.y(v));
          return (
            <g key={`${i}-${s}`}>
              <path {...markProps(lookOf(s, several))} {...fillProps(lookOf(s, several), uid, 'chart-grow-up')} d={barUp(x, a.base, bar - gap, h)} />
              <text className="chart-v" data-label={`${i}-${s}`} x={round(x + (bar - gap) / 2)} y={round(a.base - h - 0.35 * box.rem)} textAnchor="middle">
                {chartNumber(v)}
              </text>
            </g>
          );
        }),
      )}
      {names(spec, box.rem, centre, group, a.base, a.nameLine)}
    </>
  );
  return { width: box.width, height: a.total, body };
}

function drawAcross({ spec, box, uid }: DrawProps): Drawing {
  const { rem, width } = box;
  const several = spec.series.length > 1;
  const scale = chartScale(chartMax(spec));
  const nameLine = TEXT.name * rem * 1.3;
  const bar = Math.max(14, 1.15 * rem);
  const between = 0.3 * rem;
  const rowGap = 0.95 * rem;
  const valueRoom = Math.ceil((chartNumber(scale.max).length + 1) * TEXT.value * rem * LETTER + 0.6 * rem);
  const plotW = Math.max(40, width - valueRoom - 2);
  const letters = (width - 4) / (TEXT.name * rem * LETTER);
  let y = 2;
  const rows = spec.categories.map((c, i) => {
    const lines = wrap(c, letters, 3);
    const top = y;
    const barsTop = top + lines.length * nameLine + 0.25 * rem;
    y = barsTop + spec.series.length * bar + (spec.series.length - 1) * between + rowGap;
    return (
      <g key={i}>
        <text className="chart-n" data-x={i} x={1} y={round(top)}>
          {lines.map((line, n) => (
            <tspan key={n} x={1} dy={n === 0 ? nameLine * 0.8 : nameLine}>
              {line}
            </tspan>
          ))}
        </text>
        {spec.series.map((_s, s) => {
          const v = value(spec, s, i);
          const w = Math.max(1.5, (v / scale.max) * plotW);
          const by = barsTop + s * (bar + between);
          return (
            <g key={s}>
              <path {...markProps(lookOf(s, several))} {...fillProps(lookOf(s, several), uid, 'chart-grow-across')} d={barAcross(1, by, w, bar)} />
              <text className="chart-v" data-label={`${i}-${s}`} x={round(1 + w + 0.4 * rem)} y={round(by + bar / 2)} dy="0.34em">
                {chartNumber(v)}
              </text>
            </g>
          );
        })}
      </g>
    );
  });
  const bottom = y - rowGap + 2;
  return {
    width,
    height: Math.ceil(bottom),
    body: rows,
  };
}

function drawLine({ spec, box, height, uid }: DrawProps): Drawing {
  const a = axis(spec, box, height);
  const count = spec.categories.length;
  const several = spec.series.length > 1;
  const pad = Math.min(a.plotW / 2, Math.max(1.1 * box.rem, a.plotW / (count * 2)));
  const step = count > 1 ? (a.plotW - pad * 2) / (count - 1) : a.plotW;
  const x = (i: number) => (count > 1 ? a.left + pad + step * i : a.left + a.plotW / 2);
  const r = Math.max(3.5, 0.26 * box.rem);
  const body = (
    <>
      {a.grid}
      <clipPath id={`${uid}-reveal`}>
        <rect className="chart-reveal" x={0} y={0} width={box.width} height={a.total} />
      </clipPath>
      <g clipPath={`url(#${uid}-reveal)`}>
        {spec.series.map((_s, s) => {
          const look = lookOf(s, several);
          const d = spec.categories.map((_, i) => `${i === 0 ? 'M' : 'L'} ${round(x(i))} ${round(a.y(value(spec, s, i)))}`).join(' ');
          // the first series writes its values over its points, the next under, and so on: fewer collisions
          const over = s % 2 === 0;
          return (
            <g key={s}>
              <path className={`chart-line chart-s${look.colour}`} d={d} strokeDasharray={DASHES[s % DASHES.length]} />
              {spec.categories.map((_, i) => {
                const v = value(spec, s, i);
                const py = a.y(v);
                return (
                  <g key={i}>
                    <Point look={look} x={round(x(i))} y={round(py)} r={r} />
                    <text className="chart-v" data-label={`${i}-${s}`} x={round(x(i))} y={round(over ? py - r - 0.35 * box.rem : py + r + TEXT.value * box.rem)} textAnchor="middle">
                      {chartNumber(v)}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </g>
      {names(spec, box.rem, x, count > 1 ? step : a.plotW, a.base, a.nameLine)}
    </>
  );
  return { width: box.width, height: a.total, body };
}

function drawDonut({ spec, box, height, uid }: DrawProps, totalWord: string): Drawing {
  const { rem, width } = box;
  const values = spec.categories.map((_, i) => value(spec, 0, i));
  const slices = donutSlices(values);
  const total = values.reduce((s, v) => s + v, 0);
  const several = spec.categories.length > 1;
  // room all round for the percent beside each slice
  const labelW = 4 * TEXT.value * rem * LETTER + 0.5 * rem;
  const labelH = TEXT.value * rem * 1.5;
  const outer = Math.max(36, Math.min(width / 2 - labelW, height / 2 - labelH));
  const inner = outer * 0.6;
  const h = Math.ceil(outer * 2 + labelH * 2);
  const cx = width / 2;
  const cy = h / 2;
  const body = (
    <>
      <g className="chart-sweep" style={{ transformOrigin: `${round(cx)}px ${round(cy)}px` }}>
        {slices.map((slice) =>
          slice.value > 0 ? <path key={slice.index} {...markProps(lookOf(slice.index, several))} {...fillProps(lookOf(slice.index, several), uid, 'chart-slice')} d={ringPath(cx, cy, outer, inner, slice.from, slice.to)} /> : null,
        )}
      </g>
      {slices.map((slice) => {
        if (slice.value <= 0) return null;
        const angle = ((slice.from + slice.to) / 2) * 2 * Math.PI - Math.PI / 2;
        const lx = cx + (outer + 0.45 * rem) * Math.cos(angle);
        const ly = cy + (outer + 0.7 * rem) * Math.sin(angle);
        const side = Math.cos(angle);
        return (
          <text key={slice.index} className="chart-v" data-label={`${slice.index}-0`} x={round(lx)} y={round(ly)} dy="0.34em" textAnchor={side > 0.25 ? 'start' : side < -0.25 ? 'end' : 'middle'}>
            {`${slice.percent}%`}
          </text>
        );
      })}
      <text className="chart-total" data-total="" x={round(cx)} y={round(cy)} dy="0.1em" textAnchor="middle">
        {chartNumber(total)}
      </text>
      <text className="chart-t" x={round(cx)} y={round(cy + TEXT.total * rem * 0.62)} dy="0.32em" textAnchor="middle">
        {spec.unit ? `${totalWord} · ${spec.unit}` : totalWord}
      </text>
    </>
  );
  return { width, height: h, body };
}

/* ------------------------------------------------------------------------------------------------
 * The figure
 * ---------------------------------------------------------------------------------------------- */

export function Chart({ spec, testId = 'chart', height = 240, level = 3 }: ChartProps) {
  const { t } = useTranslation();
  const [asTable, setAsTable] = useState(false);
  const drawRef = useRef<HTMLDivElement>(null);
  const box = useBox(drawRef);
  const uid = `chart${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const titleId = `${uid}-title`;
  const Heading = `h${level}` as 'h2' | 'h3' | 'h4';

  const table = chartTable(spec, { share: t('chart175.share') });
  const empty = chartIsEmpty(spec);
  const donut = spec.kind === 'donut';
  const line = spec.kind === 'line';

  // the looks that need a pattern tile, and the legend: a line per series — for a donut, a line per slice
  const parts = donut ? spec.categories.length : spec.series.length;
  const several = donut ? spec.categories.length > 1 : spec.series.length > 1;
  const looks = Array.from({ length: parts }, (_, i) => lookOf(i, several));
  const showLegend = !empty && (donut || several);

  let drawing: Drawing | null = null;
  if (!empty) {
    const props: DrawProps = { spec, box, height: Math.round((height * box.rem) / STANDARD_REM), uid };
    drawing = spec.kind === 'bar' ? drawBars(props) : spec.kind === 'hbar' ? drawAcross(props) : line ? drawLine(props) : drawDonut(props, t('chart175.total'));
  }

  return (
    <figure className="chart" data-testid={testId} data-kind={spec.kind} data-view={asTable ? 'table' : 'chart'}>
      <div className="chart-head">
        <div className="chart-words">
          <Heading className="chart-title" id={titleId}>
            {spec.title}
          </Heading>
          {spec.note ? <p className="chart-note">{spec.note}</p> : null}
        </div>
        <button type="button" className="btn secondary sm chart-toggle no-print" data-testid="chart-toggle" aria-pressed={asTable} aria-describedby={titleId} onClick={() => setAsTable((v) => !v)}>
          {asTable ? t('chart175.showChart') : t('chart175.showTable')}
        </button>
      </div>

      <div className="chart-draw" ref={drawRef} hidden={asTable}>
        {drawing ? (
          <svg className="chart-svg" width={drawing.width} height={drawing.height} viewBox={`0 0 ${drawing.width} ${drawing.height}`} aria-hidden="true" role="presentation" focusable="false">
            {several && !line ? (
              <defs>
                {looks.map((look) => (
                  <PatternDef key={look.index} id={`${uid}-p${look.index}`} look={look} />
                ))}
              </defs>
            ) : null}
            {drawing.body}
          </svg>
        ) : (
          <p className="chart-empty" data-testid="chart-empty">
            {t('chart175.empty')}
          </p>
        )}
        {showLegend ? (
          <ul className="chart-legend" data-testid="chart-legend" aria-label={t('chart175.legend')}>
            {looks.map((look) => (
              <li key={look.index} data-pattern={look.pattern}>
                <Swatch look={look} uid={uid} line={line} />
                {donut ? (
                  <span>
                    <span className="chart-legend-name">{table.rows[look.index]?.[0]}</span> <span className="chart-legend-value">{`${table.rows[look.index]?.[1]}${spec.unit ? ` ${spec.unit}` : ''} · ${table.rows[look.index]?.[2]}`}</span>
                  </span>
                ) : (
                  <span className="chart-legend-name">{spec.series[look.index]?.name}</span>
                )}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {/* the one table: read always, seen when asked for — and on paper, under the drawing */}
      <div className={asTable ? 'chart-numbers table-wrap' : 'chart-numbers sr-only'} {...(asTable ? { tabIndex: 0, role: 'region', 'aria-label': spec.title } : {})}>
        <table className="table chart-table" data-testid="chart-table">
          <caption>{table.caption}</caption>
          <thead>
            <tr>
              {table.head.map((h, i) => (
                <th key={i} scope="col">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, r) => (
              <tr key={r}>
                {row.map((cell, i) =>
                  i === 0 ? (
                    <th key={i} scope="row">
                      {cell}
                    </th>
                  ) : (
                    <td key={i}>{cell}</td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}
